import queue from '@xfe/xoyo-queue';
import jsonp from '@xfe-team/utils/src/jsonp-util';
import wrapAfterServicePromise from '@src/utils/wrap-after-service-promise';
import { ZT_ROOT_ADDRESS } from '@src/constants/urls';

export default class ServiceFactory {
  /**
   * @param {object} queueMap - 排队映射, 如: {'jx3/ticket190728/create_order': 'jx3master'}, 详细请参考: https://gitlab.xsjcs.cn/xfe/xfe-iframe-application/blob/request-queue/parent/README.md
   * @param {boolean} optimizeConcurrentSameRequest - 优化相同并发的请求
   * @param {number} optimizeUsingCache - 优化缓存时间, 默认 0, 单位毫秒, 设置大于0值启用, 用于确保同一个请求能够被正确被缓存和获取
   */
  constructor({ queueMap, optimizeConcurrentSameRequest, optimizeUsingCache = 0 }) {
    this.queueMap = queueMap;
    this.beforeRequests = [];
    this.afterRequestSuccessList = [];
    this.afterRequestErrorList = [];
    this.optimizeConcurrentSameRequest = optimizeConcurrentSameRequest;
    optimizeConcurrentSameRequest && (this.concurrentRequestQueue = {});
    this.optimizeUsingCache = optimizeUsingCache;
    optimizeUsingCache && (this.cache = {});
  }

  /**
   * 获取排队映射 key 值
   * @private
   *
   * @param {string} url - 请求 url 地址
   * 如: 请求地址为 http://zt-ws.xoyo.com/zt/2019/08/test/get-user-info
   *
   * 当 this.queueMap = { 'test/get-user-info': 'jx3master' }
   * 返回: 'jx3master'
   *
   * @returns {string}
   */
  getQueueMapKey(url) {
    const queueMap = this.queueMap || {};
    /**
     * 用于匹配 url 末段是否一致
     */
    return Object.keys(queueMap).find(mapUrl => {
      const mapUrlLength = mapUrl.length;
      // 匹配命中最后字符串是否一致
      return url.slice(-mapUrlLength) === mapUrl;
    });
  }

  /**
   * 请求
   * @public
   *
   * @param {string} rawUrl - 请求地址
   * @param {object} rawParams - 请求参数
   */
  request(rawUrl, rawParams, makeNativeJsonpRequest) {
    return this.handleCache(
      () => JSON.stringify({ rawUrl, rawParams }),
      async () => {
        const { url, params } = await this.interceptBeforeRequest({ rawUrl, rawParams });
        const queueMapKey = this.getQueueMapKey(url);
        let requestPromise;
        if (queueMapKey) {
          requestPromise = this.makeQueueRequest(this.queueMap[queueMapKey], params);
        } else {
          requestPromise = (makeNativeJsonpRequest || this.makeNativeJsonpRequest)(url, params);
        }
        return requestPromise
          .then(responseValue => {
            return this.interceptAfterRequestSuccess({ requestOptions: { url, params }, responseValue });
          })
          .catch(async errorResponseValue => {
            throw await this.interceptAfterRequestError({ requestOptions: { url, params }, errorResponseValue });
          });
      }
    );
  }

  post(rawUrl, rawParams) {
    return this.request(rawUrl, rawParams, this.makePostRequest);
  }

  /**
   * 创建缓存
   * @param {string} key - 缓存 key 值
   * @param {object} promise - 返回值 promise
   * @return {{response: object, createTime: number}}
   */
  createCache(key, promise) {
    this.cache[key] = {
      createTime: +new Date(),
      promise
    };
  }

  /**
   * 缓存是否过期
   * @param {object} cache - 缓存
   * @return {boolean}
   */
  isCacheExpired(cache) {
    return !(cache && cache.createTime + this.optimizeUsingCache > +new Date());
  }

  /**
   * 获取缓存
   * @param key
   * @return {*}
   */
  getCache(key) {
    return this.cache[key];
  }

  /**
   * 清除 cache
   * @param {string} key - 缓存 key 值
   */
  clearCache(key) {
    const { cache } = this;
    cache[key] && delete cache[key];
  }

  /**
   * 清除 cache
   */
  clearAllCache() {
    this.cache = {};
  }

  /**
   * 处理缓存
   * @param {Function} getKey - 获取 cache key
   * @param {Function} action - 实际请求
   * @return {any | Object | AuthenticatorResponse}
   */
  handleCache(getKey, action) {
    if (this.optimizeUsingCache) {
      const key = getKey();
      const cache = this.getCache(key);
      if (!this.isCacheExpired(cache)) {
        return cache.promise;
      }
      this.clearCache(key);
      const promise = action();
      this.createCache(key, promise);
      promise.catch(() => {
        this.clearCache(key);
      });
      return promise;
    }
    return action();
  }

  /**
   * 处理并发请求, 确保同一个请求只能有一个 out going 请求
   * @param {Function} getKey - 请求唯一 key, 使用方法目的为了性能优化
   * @param {Function} action - 请求具体实现方法
   * @return {Promise}
   */
  handleConcurrentRequest(getKey, action) {
    if (!this.optimizeConcurrentSameRequest) {
      return action();
    }
    const key = getKey();
    let promise = this.concurrentRequestQueue[key];
    if (!promise) {
      this.concurrentRequestQueue[key] = action();
      promise = this.concurrentRequestQueue[key];
    } else {
      return promise;
    }
    promise.finally(() => {
      delete this.concurrentRequestQueue[key];
    });
    return promise;
  }

  /**
   * 请求前拦截
   * @private
   *
   * @param {string} rawUrl - 请求地址
   * @param {object} rawParams - 请求参数
   */
  interceptBeforeRequest({ rawUrl, rawParams }) {
    return this.beforeRequests.reduce(async (previousRequestOptions, beforeRequest) => {
      const nextRequestOptions = await beforeRequest(await previousRequestOptions);
      if (nextRequestOptions === undefined) {
        return previousRequestOptions;
      } else {
        return nextRequestOptions;
      }
    }, Promise.resolve({ url: rawUrl, params: rawParams }));
  }

  /**
   * 拦截请求成功后
   * @private
   *
   * @param {{rawUrl, object}} requestOptions - 请求参数, 开发者可以使用该参数进行相关拦截判断
   * @param {object} responseValue - 请求参数
   *
   * 如果拦截函数在这个过程中返回 undefined, 则判断为取消当次拦截, 使用上一次拦截返回的结果
   *
   * @returns {object} - 类型应等同 responseValue, 作为拦截后的返回值
   */
  async interceptAfterRequestSuccess({ requestOptions, responseValue }) {
    const { responseValue: result } = await this.afterRequestSuccessList.reduce(
      async (previousAfterRequestSuccessParams, afterRequestSuccess) => {
        const nextRequestSuccessReturnResponseValue = await afterRequestSuccess(previousAfterRequestSuccessParams);
        if (nextRequestSuccessReturnResponseValue === undefined) {
          return previousAfterRequestSuccessParams;
        } else {
          return { requestOptions, responseValue: nextRequestSuccessReturnResponseValue };
        }
      },
      { requestOptions, responseValue }
    );
    return result;
  }

  /**
   * 拦截请求成功后, 注意: 当前方法设计暂时不支持逆转, 即请求认定为失败后无法逆转会成功
   * @private
   *
   * 如果拦截函数在这个过程中返回 undefined, 则判断为取消当次拦截, 使用上一次拦截返回的结果
   *
   * @param {{rawUrl, object}} requestOptions - 请求参数, 开发者可以使用该参数进行相关拦截判断
   * @param {object} errorResponseValue - 请求参数
   */
  async interceptAfterRequestError({ requestOptions, errorResponseValue }) {
    const { errorResponseValue: result } = await this.afterRequestErrorList.reduce(
      async (previousAfterRequestErrorParams, afterRequestError) => {
        const nextRequestErrorReturnResponseValue = await afterRequestError(previousAfterRequestErrorParams);
        if (nextRequestErrorReturnResponseValue === undefined) {
          return previousAfterRequestErrorParams;
        } else {
          return { requestOptions, responseValue: nextRequestErrorReturnResponseValue };
        }
      },
      { requestOptions, errorResponseValue }
    );
    return result;
  }

  /**
   * 请求排队请求
   * @private
   *
   * @param {string} scope - 排队 scope, 具体请阅读: https://gitlab.xsjcs.cn/xfe/xfe-iframe-application/blob/request-queue/parent/README.md
   * @param {object} params - 请求参数
   * @returns {Promise<any>}
   */
  makeQueueRequest(scope, params) {
    return wrapAfterServicePromise(() => queue({ scope, params, host: ZT_ROOT_ADDRESS, preAuth: { url: '' } }).promise, { requestOptions: params });
  }

  /**
   * 发起普通原始 jsonp 请求
   * @private
   *
   * @param {string} url - 请求地址
   * @param {object} params - 请求参数
   * @returns {Promise<ServiceResponse>}
   */
  makeNativeJsonpRequest(url, params) {
    return jsonp(url, params);
  }

  makePostRequest(url, params) {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line no-undef
      $.post(url, data => {
        resolve(data);
      }).fail(() => reject());
    });
  }

  /**
   * 创建服务
   * @protected
   */
  createService() {
    throw new Error('please implement');
  }
}
